import React from "react";
import { useNavigate, useLocation } from "react-router-dom";

import icoOverview from "../assets/Overview_Icon.png";
import icoNew from "../assets/New-Onboarding_Icon.png";
import icoManage from "../assets/Manage-Requests_Icon.png";
import icoApprovals from "../assets/Approvas_Icon.png";
import icoAttStatus from "../assets/search.svg";
import icoHelp from "../assets/help-circle.svg";
import icoExceptions from "../assets/tool.svg";

function NavIcon({ src, alt, fallback = "•" }) {
    const [err, setErr] = React.useState(false);
    if (err || !src) return <span className="ico-fallback" aria-hidden="true">{fallback}</span>;
    return (
        <img
            src={src}
            alt=""
            aria-hidden="true"
            className="ico-img"
            onError={() => setErr(true)}
            draggable="false"
        />
    );
}

export function Sidebar({ activeKey, collapsed = false }) {
    const nav = useNavigate();
    const loc = useLocation();
    const role = (sessionStorage.getItem("tb_role") || "").trim();
    const isReadOnlyRole = role.toLowerCase() === "read only";

    // base items
    const items = [
        { key: "overview", label: "Overview", icon: icoOverview, fallback: "🏠", path: "/dashboard" },
        { key: "new", label: "New Onboarding", icon: icoNew, fallback: "➕", path: "/onboarding/new" },
        { key: "manage", label: "Manage Requests", icon: icoManage, fallback: "📋", path: "/manage-requests" },
        { key: "approvals", label: "Approvals", icon: icoApprovals, fallback: "✅", path: "/approvals" },

        // ✅ FIXED
        { key: "att-status-check", label: "AT&T Onboarding Status", icon: icoAttStatus, fallback: "🔎", path: "/att-status-check" },

        { key: "reporting", label: "Reporting Dashboard", icon: null, fallback: "📊", path: "/reporting" },

        { key: "help", label: "Help", icon: icoHelp, fallback: "❓", path: "/help" },
    ];

    const visibleItems = isReadOnlyRole
        ? items.filter((it) => ["overview", "att-status-check", "reporting", "help"].includes(it.key))
        : items;

    // add Exceptions tab only for DTV OPS Admin / Employee Supplier Admin
    if (!isReadOnlyRole && (role === "DTV OPS Admin" || role === "Employee Supplier Admin")) {
        visibleItems.push({
            key: "exceptions",
            label: "Exceptions",
            icon: null,
            fallback: "🛠️",
            path: "/ops/exceptions",
        });
    }

    // Ignore trailing slashes & query strings; mark base and its children as active
    const isActivePath = (basePath) => {
        const pathname = ((loc.pathname || "/").split("?")[0] || "/").replace(/\/+$/, "");
        const base = (basePath || "/").replace(/\/+$/, "");
        return pathname === base || pathname.startsWith(base + "/");
    };

    // Only honor activeKey if it's a known key OR exact path; else fall back to URL detection
    const knownKeys = new Set(visibleItems.map(i => i.key));
    const pathToKey = new Map(visibleItems.map(i => [i.path, i.key]));
    const forcedKey =
        activeKey && (
            knownKeys.has(activeKey)
                ? activeKey
                : (pathToKey.has(activeKey) ? pathToKey.get(activeKey) : null)
        );

    const isActive = (k, p) => (forcedKey ? forcedKey === k : isActivePath(p));

    const go = (p) => nav(p);
    const onKey = (e, p) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            nav(p);
        }
    };

    return (
        <ul className={`tb-nav ${collapsed ? "tb-nav-collapsed" : ""}`} role="navigation" aria-label="Main">
            {visibleItems.map((it) => {
                const active = isActive(it.key, it.path);
                return (
                    <li
                        key={it.key}
                        className={active ? "active" : ""}
                        tabIndex={0}
                        role="link"
                        aria-current={active ? "page" : undefined}
                        onClick={() => go(it.path)}
                        onKeyDown={(e) => onKey(e, it.path)}
                        title={it.label}
                    >
                        <span className="ico" aria-hidden="true">
                            <NavIcon src={it.icon} alt={it.label} fallback={it.fallback} />
                        </span>
                        {!collapsed && <span className="lbl">{it.label}</span>}
                    </li>
                );
            })}
        </ul>
    );
}
